<template>
  <div class="client-devices">
    <el-card>
      <div
          slot="header"
          class="clearfix">
        <span class="header-item">{{ $t('client_devices.title') }}</span>
        <span class="ring-card__header-buttons">
          <UnlinkDevices
              v-acl.enable="{permission: 'unlink', on:'ClientDevices'}"
              :customer-id="customerId"
              @onSucceed="fetch">
            <span>Unlink All</span>
          </UnlinkDevices>
          <el-divider direction="vertical" />

          <RefreshButton @click.native="fetch(true)" />
        </span>
      </div>
      <div class="card-content">
        <el-table
            ref="clientDevicesTable"
            v-loading="ajax.working"
            class="client-device-table"
            :data="clientDevices"
            @expand-change="onExpandRow"
            @cell-click="toggleRowExpansion">
          <el-table-column type="expand">
            <template slot-scope="scope">
              <ul style="margin: 0; padding: 0 50px; list-style: none;">
                <li
                    v-for=" (value, key, index) in scope.row.metadata"
                    :key="index"
                    style="float: left; width: 50%; margin: 5px -10px; padding: 5px 10px;">
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="flex: 1;">{{ startCase(key) }}:  </strong>
                    <span style="flex: 2;">{{ value }}</span>
                  </div>
                </li>
              </ul>
            </template>
          </el-table-column>
          <el-table-column
              v-for="column in metaTable"
              v-if="showColumn({id: column.id})"
              :key="column.prop"
              :prop="column.prop"
              :width="column.width"
              :label="column.label">
            <template
                slot-scope="scope"
                style="padding-right: 3px">
              <section>
                {{ get(scope.row, column.prop, '') }}
              </section>
            </template>
          </el-table-column>
          <el-table-column
              min-width="20"
              align="center"
              label="Passkey Enabled">
            <template
                slot-scope="scope">
              <i
                  v-if="scope.row.passkey && scope.row.passkey.enabled === true"
                  style="font-size: large; font-weight: bold;"
                  class="el-icon-check"></i>
            </template>
          </el-table-column>
          <el-table-column
              min-width="20"
              align="center"
              :label="$t('client_devices.signed_in')">
            <template
                slot-scope="scope">
              <el-tooltip
                  effect="dark"
                  placement="top"
                  :content="scope.row.signed_in ? 'Customer signed in' : 'Customer not signed in yet.'">
                <i
                    style="font-size: large; font-weight: bold;"
                    :class="scope.row.signed_in ? 'el-icon-check' : 'el-icon-close'"></i>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column
              min-width="30"
              align="center"
              :label="$t('client_devices.allows_screensharing')">
            <template
                slot-scope="scope">
              <i
                  style="font-size: large; font-weight: bold;"
                  :class="scope.row.screensharing ? 'el-icon-check' : 'el-icon-close'"></i>
            </template>
          </el-table-column>
          <el-table-column
              prop="hardware_id"
              label="Actions"
              width="120">
            <template slot-scope="scope">
              <div v-if="scope.row.hardware_id">
                <UnlinkDevices
                    v-acl.enable="{permission: 'unlink', on:'ClientDevices'}"
                    :device-id="scope.row.hardware_id"
                    :customer-id="customerId"
                    :app-brand="scope.row.app_brand"
                    tooltip="Unlink Device"
                    @onSucceed="fetch">
                  <i class="el-icon-delete"></i>
                </UnlinkDevices>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script>
import { get, keys, startCase, isEmpty } from "lodash";
import UnlinkDevices from "@/containers/Customer/Profile/ClientDevices/UnlinkDevices";
import { EventBus } from "@/eventBus";
import axios from "axios";
import { createNamespacedHelpers } from "vuex";
import RefreshButton from "@/containers/Customer/RefreshButton";

const { mapActions: accountActions } = createNamespacedHelpers("db/account");

export default {
  name: "ClientDevices",
  components: {
    UnlinkDevices,
    RefreshButton,
  },
  props: {
    customer: { type: Object, default: () => {} },
    columns: {
      type: Array,
      default: () => {
        // return ["app_brand", "hardware_id", "os", "signed_in"];
        return ["device_model", "os", "app_version", "signed_in"];
      },
      // TODO: validate that array contain only valid columns
    },
  },
  data() {
    return {
      clientDevices: [],
      ajax: {
        working: false,
      },
      metaTable: [
        {
          id: "app_brand",
          prop: "app_brand",
          label: this.$t("client_devices.app_brand"),
        },
        {
          id: "hardware_id",
          prop: "hardware_id",
          label: this.$t("client_devices.hardware_id"),
        },
        {
          id: "device_model",
          prop: "metadata.device_model",
          label: "Device Model",
          width: 300,
        },
        {
          id: "os",
          prop: "os",
          label: "OS",
          width: 120,
        },
        {
          id: "push_token",
          prop: "push_token",
          label: "Token",
          width: 250,
        },
        {
          id: "os_version",
          prop: "metadata.os_version",
          label: "OS Version",
          width: 150,
        },
        {
          id: "app_version",
          prop: "metadata.app_version",
          label: "App Version",
          width: 230,
        },
      ],
    };
  },
  computed: {
    customerId() {
      return this.$store.getters["db/account/customerId"];
    },
  },
  watch: {
    customerId(value) {
      if (value) this.fetch();
    },
  },
  created() {
    this.fetch();
    EventBus.$on("customer-account-locked", () => {
      this.fetch();
    });
    EventBus.$on("customer-account-unlocked", () => {
      this.fetch();
    });
  },
  methods: {
    ...accountActions(["setScreensharing"]),
    get,
    startCase,
    fetch() {
      this.ajax.working = true;
      const search = {
        customer_id: this.customerId,
      };
      axios
        .get("v2/client_devices", { params: search })
        .then((response) => {
          let devices = get(response, "data", []);
          this.clientDevices = devices.map((device) => {
            device.expanded = false;
            device.screensharing = this.screensharingValidaVersion(device);
            return device;
          });
          this.setScreensharing({
            enabled:
              this.clientDevices.filter((device) => device.screensharing)
                .length > 0,
          });
        })
        .catch((error) => {})
        .finally(() => (this.ajax.working = false));
    },
    showColumn({ id }) {
      return this.columns.includes(id);
    },
    toggleRowExpansion(row, column) {
      if (
        column.property === "push_token" &&
        row.expanded &&
        !isEmpty(row.push_token)
      )
        return;
      if (column.label === "Actions") return;
      this.$refs.clientDevicesTable.toggleRowExpansion(row);
    },
    onExpandRow(row) {
      row.expanded = !get(row, "expanded", false);
    },
    pushTokenColumnCss(row) {
      let state = get(row, "expanded", false) ? "expanded" : "collapsed";
      return `push-token__${state}`;
    },
    screensharingValidaVersion(device) {
      let version = get(device, "metadata.app_version", null);
      if (version === null) return false;

      version = version.split(" ")[0];
      version = version.replace(/[^0-9.]/g, "");
      const os = device.os.toLowerCase();

      let validVersion = false;
      if (os === "ios") {
        validVersion = this.isVersionHigherOrEqual(version, "5.30");
      } else if (os === "android") {
        validVersion = this.isVersionHigherOrEqual(version, "3.30");
      }

      return validVersion;
    },
    isVersionHigherOrEqual(ver1, ver2) {
      ver1 = ver1
        .split(".")
        .map((s) => s.padStart(10))
        .join(".");
      ver2 = ver2
        .split(".")
        .map((s) => s.padStart(10))
        .join(".");
      return ver1 >= ver2;
    },
  },
};
</script>

<style lang="scss">
.success {
  color: green;
}
.error {
  color: red;
}
.client-device-table {
  .push-token__expanded {
    display: inline-block;
    padding: 0 17px 0 0;
    user-select: all;
    word-break: break-all;
  }
  .push-token__collapsed {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
}
.el-table th.el-table__cell > .cell {
  word-break: break-word;
}
</style>
